// src/components/layout/MainLayout.jsx
import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Head } from 'components/Head';
import LogoImg from 'assets/logo.png';

const navItems = [
  { label: 'Products', to: '/products' },
  { label: 'Company', to: '/company' },
  // { label: 'Contact Us', to: '/contact-us' },
];

export const MainLayout = ({ title, description, children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navigate = useNavigate();

  return (
    <>
      <Head title={title} description={description} />

      <div className="min-h-screen bg-slate-950 text-slate-100">
        {/* Background effects */}
        <div aria-hidden="true" className="pointer-events-none fixed inset-0 overflow-hidden">
          <div className="absolute -top-48 -left-32 h-96 w-96 rounded-full bg-[#9D5666]/20 blur-3xl" />
          <div className="absolute top-40 -right-40 h-[420px] w-[420px] rounded-full bg-cyan-500/10 blur-3xl" />
          <div className="absolute bottom-[-180px] left-1/2 h-80 w-[520px] -translate-x-1/2 rounded-[999px] bg-gradient-to-r from-[#9D5666]/10 via-[#BB7283]/10 to-[#A5A9AA]/10 blur-3xl" />
        </div>

        {/* Shell */}
        <div className="relative flex min-h-screen flex-col">
          {/* Nav */}
          <header className="sticky top-0 z-40 border-b border-slate-800/60 bg-slate-950/70 backdrop-blur-xl">
            <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
              {/* Left: Logo + Brand */}
              <Link to="/" className="group flex items-center gap-2">
                <div className="relative flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-700/70 bg-slate-900/80 shadow-[0_0_40px_rgba(56,189,248,0.15)]">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-cyan-500/40 via-[#9D5666]/30 to-[#BB7283]/40 opacity-0 blur-sm transition group-hover:opacity-100" />
                  <img src={LogoImg} alt="northup limited" className="" />
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
                    Northup
                  </span>
                  <span className="text-[11px] font-medium text-slate-500">
                    Intelligent Business Systems
                  </span>
                </div>
              </Link>

              {/* Center: Nav items (desktop) */}
              <div className="hidden items-center gap-6 md:flex">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      [
                        'relative text-sm font-medium transition-colors',
                        isActive ? 'text-slate-50' : 'text-slate-400 hover:text-slate-100',
                      ].join(' ')
                    }
                  >
                    <span>{item.label}</span>
                    <span className="absolute inset-x-1 -bottom-1 h-px origin-center scale-x-0 bg-gradient-to-r from-cyan-400 to-purple-400 transition-transform duration-300 group-hover:scale-x-100 md:group-hover:scale-x-100" />
                  </NavLink>
                ))}
              </div>

              {/* Right: CTA + Menu toggle */}
              <div className="flex items-center gap-3">
                {/* <button className="hidden items-center rounded-full border border-slate-700/70 bg-slate-900/80 px-4 py-1.5 text-xs font-medium text-slate-200/80 shadow-sm transition hover:border-slate-500 hover:text-slate-50 sm:flex">
                  <span className="mr-2 h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                  Book demo
                </button> */}

                <button
                  onClick={() => navigate('/contact')}
                  className="hidden rounded-full bg-gradient-to-r from-[#9D5666] via-[#C4C5C7] to-[#9D5666] px-4 py-1.5 text-xs font-semibold tracking-tight text-slate-950 shadow-[0_0_25px_rgba(56,189,248,0.45)] transition hover:brightness-110 sm:inline-flex"
                >
                  Contact Us
                </button>

                {/* Mobile menu toggle */}
                <button
                  type="button"
                  onClick={() => setIsMenuOpen((prev) => !prev)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-700/80 bg-slate-950/90 text-slate-200 md:hidden"
                >
                  <span className="sr-only">Toggle navigation</span>
                  <div className="flex flex-col items-center justify-center gap-[5px]">
                    <span
                      className={`h-[2px] w-4 rounded-full bg-slate-200 transition ${
                        isMenuOpen ? 'translate-y-[3px] rotate-45' : ''
                      }`}
                    />
                    <span
                      className={`h-[2px] w-4 rounded-full bg-slate-400 transition ${
                        isMenuOpen ? 'opacity-0' : ''
                      }`}
                    />
                    <span
                      className={`h-[2px] w-4 rounded-full bg-slate-200 transition ${
                        isMenuOpen ? '-translate-y-[3px] -rotate-45' : ''
                      }`}
                    />
                  </div>
                </button>
              </div>
            </nav>

            {/* Mobile nav */}
            <motion.div
              initial={false}
              animate={isMenuOpen ? 'open' : 'closed'}
              variants={{
                open: { height: 'auto', opacity: 1 },
                closed: { height: 0, opacity: 0 },
              }}
              transition={{ duration: 0.24, ease: 'easeInOut' }}
              className="overflow-hidden border-t border-slate-800/80 bg-slate-950/95 md:hidden"
            >
              <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setIsMenuOpen(false)}
                    className={({ isActive }) =>
                      [
                        'text-sm font-medium',
                        isActive ? 'text-slate-50' : 'text-slate-400 hover:text-slate-100',
                      ].join(' ')
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}

                <div className="mt-2 flex flex-col gap-2">
                  {/* <button className="w-full rounded-full border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-medium text-slate-100">
                    Book demo
                  </button> */}
                  <button
                    onClick={() => navigate('/contact')}
                    className="w-full rounded-full bg-gradient-to-r from-[#9D5666] via-[#C4C5C7] to-[#9D5666] px-3 py-2 text-xs font-semibold text-slate-950"
                  >
                    Contact Us
                  </button>
                </div>
              </div>
            </motion.div>
          </header>

          {/* Main content */}
          <motion.main
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
            className="mx-auto flex w-full max-w-7xl flex-1 flex-col px-4 pb-10 pt-10 sm:px-6 lg:px-8 lg:pb-16 lg:pt-14"
          >
            {children}
          </motion.main>

          {/* Footer */}
          <footer className="border-t border-slate-800/70 bg-slate-950/80">
            <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-5 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
              <span>© {new Date().getFullYear()} Northup Technologies. All rights reserved.</span>
              <div className="flex flex-wrap items-center gap-4">
                <button className="rounded-full border border-slate-700/80 px-3 py-1 text-[11px] font-medium text-slate-300 backdrop-blur hover:border-slate-500 hover:text-slate-50">
                  Status:{' '}
                  <span className="ml-1 inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  <span className="ml-1">All systems operational</span>
                </button>
                <div className="flex gap-3">
                  <Link to="/privacy" className="hover:text-slate-300">
                    Privacy
                  </Link>
                  <Link to="/terms" className="hover:text-slate-300">
                    Terms
                  </Link>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </>
  );
};
